/**
 * Sprint 5 — operators, cards, documents and permissions (ADR 0009).
 *
 * The database function `authlist_entries(device)` is the source of truth for what a device receives;
 * `authorizedEntries` below implements the SAME rule so the console can explain it, and a database test
 * checks both agree. Dates are YYYY-MM-DD (local business dates, compared as strings).
 */
import { cardUid as cardUidSchema, type AuthEntry } from "./icd/schemas.js";
import type { Site, Vehicle, VehicleType } from "./domain.js";

export const CARD_TECHS = ["mifare", "hid", "em125"] as const;
export type CardTech = (typeof CARD_TECHS)[number];

export const CARD_STATUSES = ["active", "lost", "revoked"] as const;
export type CardStatus = (typeof CARD_STATUSES)[number];

export const DOCUMENT_KINDS = ["operator_certificate", "driving_licence", "training", "other"] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];

/** Personal data (KVKK): name and employee number; anonymised six months after `leftOn`. */
export type Operator = {
  id: string;
  tenantId: string;
  /** Home site (visibility); null = visible to the whole tenant. */
  siteId: string | null;
  fullName: string;
  employeeNo: string | null;
  active: boolean;
  /** Last working day; the operator stays authorised up to and including this date. */
  leftOn: string | null;
  anonymized: boolean;
};

export type OperatorCard = {
  id: string;
  tenantId: string;
  operatorId: string;
  uid: string;
  tech: CardTech;
  status: CardStatus;
  createdAt: string;
};

export type OperatorDocument = {
  id: string;
  tenantId: string;
  operatorId: string;
  kind: DocumentKind;
  /** Free text for "other" (or a note such as the certificate class). */
  label: string | null;
  validUntil: string;
  /** A required document that expired suspends the operator. */
  required: boolean;
};

/** Either a site (with all its sub-sites, optionally one vehicle type) or a single vehicle. */
export type OperatorPermission = {
  id: string;
  tenantId: string;
  operatorId: string;
  siteId: string | null;
  vehicleType: VehicleType | null;
  vehicleId: string | null;
  validUntil: string | null;
};

/** Days before expiry at which the console warns (the e-mail reminders follow in Sprint 6). */
export const DOCUMENT_WARNING_DAYS = [30, 15, 7] as const;

export const OPERATOR_STATUSES = ["active", "expiring", "suspended", "inactive", "left"] as const;
export type OperatorStatus = (typeof OPERATOR_STATUSES)[number];

/** Normalise a card UID as readers print it ("04:a1:b2 c3" -> "04A1B2C3"); null if not a valid UID. */
export function normalizeCardUid(raw: string): string | null {
  const s = raw.replace(/[\s:.-]/g, "").toUpperCase();
  return cardUidSchema.safeParse(s).success ? s : null;
}

/** Whole days from `today` to `date` (negative = in the past). */
export function daysUntil(date: string, today: string): number {
  return Math.round((Date.parse(`${date}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000);
}

/** Today's business date in a time zone (default Turkey) as YYYY-MM-DD. */
export function businessDate(now: Date, timeZone = "Europe/Istanbul"): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

export function hasLeft(op: Pick<Operator, "leftOn">, today: string): boolean {
  return op.leftOn !== null && op.leftOn < today;
}

/** Earliest expiry among required documents, or null when none is required. */
export function requiredDocumentsUntil(docs: OperatorDocument[]): string | null {
  let min: string | null = null;
  for (const d of docs) if (d.required && (min === null || d.validUntil < min)) min = d.validUntil;
  return min;
}

export function operatorStatus(op: Operator, docs: OperatorDocument[], today: string): OperatorStatus {
  if (op.anonymized || hasLeft(op, today)) return "left";
  if (!op.active) return "inactive";
  const until = requiredDocumentsUntil(docs.filter((d) => d.operatorId === op.id));
  if (until !== null && until < today) return "suspended";
  if (until !== null && daysUntil(until, today) <= DOCUMENT_WARNING_DAYS[0]) return "expiring";
  return "active";
}

/** Is `siteId` the site itself or one of its sub-sites (walking parents)? */
export function siteWithin(sites: Pick<Site, "id" | "parentId">[], siteId: string, ancestorId: string): boolean {
  const byId = new Map(sites.map((s) => [s.id, s]));
  let cur: string | null = siteId;
  for (let guard = 0; cur && guard < 64; guard++) {
    if (cur === ancestorId) return true;
    cur = byId.get(cur)?.parentId ?? null;
  }
  return false;
}

export function permissionCovers(p: OperatorPermission, vehicle: Pick<Vehicle, "id" | "siteId" | "type">, sites: Pick<Site, "id" | "parentId">[]): boolean {
  if (p.vehicleId) return p.vehicleId === vehicle.id;
  if (!p.siteId) return false;
  return siteWithin(sites, vehicle.siteId, p.siteId) && (p.vehicleType === null || p.vehicleType === vehicle.type);
}

export type AuthorizationInput = {
  operators: Operator[];
  cards: OperatorCard[];
  documents: OperatorDocument[];
  permissions: OperatorPermission[];
  sites: Pick<Site, "id" | "parentId">[];
};

export type AuthorizedOperator = { operator: Operator; until: string | null; cards: OperatorCard[] };

/**
 * Operators allowed to start `vehicle` on `today`, with the date their authorisation ends:
 * the latest matching permission (open-ended wins) capped by the earliest required document.
 * Expired permissions/documents, inactive or departed operators are left out.
 */
export function authorizedOperators(vehicle: Pick<Vehicle, "id" | "siteId" | "type" | "tenantId">, input: AuthorizationInput, today: string): AuthorizedOperator[] {
  const out: AuthorizedOperator[] = [];
  for (const op of input.operators) {
    if (op.tenantId !== vehicle.tenantId || !op.active || op.anonymized || hasLeft(op, today)) continue;
    const perms = input.permissions.filter(
      (p) => p.operatorId === op.id && (p.validUntil === null || p.validUntil >= today) && permissionCovers(p, vehicle, input.sites),
    );
    if (perms.length === 0) continue;
    const permUntil = perms.some((p) => p.validUntil === null) ? null : perms.map((p) => p.validUntil!).sort().at(-1)!;
    const docUntil = requiredDocumentsUntil(input.documents.filter((d) => d.operatorId === op.id));
    if (docUntil !== null && docUntil < today) continue;
    const leftCap = op.leftOn;
    const until = [permUntil, docUntil, leftCap].filter((x): x is string => x !== null).sort()[0] ?? null;
    const cards = input.cards.filter((c) => c.operatorId === op.id && c.status === "active");
    out.push({ operator: op, until, cards });
  }
  return out.sort((a, b) => a.operator.fullName.localeCompare(b.operator.fullName, "tr"));
}

/** The device list for a vehicle: one entry per active card, sorted by UID (stable for hashing). */
export function authorizedEntries(vehicle: Pick<Vehicle, "id" | "siteId" | "type" | "tenantId">, input: AuthorizationInput, today: string): AuthEntry[] {
  const entries: AuthEntry[] = [];
  for (const a of authorizedOperators(vehicle, input, today)) {
    for (const c of a.cards) entries.push(a.until ? { uid: c.uid, until: a.until } : { uid: c.uid });
  }
  return entries.sort((x, y) => (x.uid < y.uid ? -1 : x.uid > y.uid ? 1 : 0));
}
